import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Trash2, Shield, Settings, Clock, AlertCircle, Check } from "lucide-react";
import { Button } from "./Button";
import { Card } from "./card";
import { Input } from "./input";
import { Textarea } from "./Textarea";
import UserDropdown from "./UserDropdown";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";
import { 
	DepositCreateDto, 
	Deposit, 
	DepositBackendResponse,
	DepositCategoryTypeEnum, 
	DepositStatusEnum, 
	DepositRuleTypeEnum,
	DepositRule,
	DepositException,
	DepositRuleCreateDto,
	DepositExceptionCreateDto,
	DepositUserRef,
	RoleEnum
} from "../../modules/shared/model";
import { 
	createDeposit, 
	updateDeposit, 
	clearDepositError,
	createDepositRule,
	updateDepositRule,
	deleteDepositRule,
	createDepositException,
	updateDepositException,
	deleteDepositException,
	fetchDeposits
} from "../../redux/Slices/depositSlice";

interface Props {
	isOpen: boolean;
	onClose: () => void;
	deposit?: Deposit | DepositBackendResponse | null;
}

const DepositDialog: React.FC<Props> = ({ isOpen, onClose, deposit }) => {
	const dispatch = useAppDispatch();
	const { loading, error } = useAppSelector((s: any) => s.deposits);

	const isEditMode = !!deposit;
	
	const [formData, setFormData] = useState<DepositCreateDto>({
		requireApproval: true,
		instantRelease: false,
		disputeWindow: 24,
		autoRelease: true,
		releaseConditions: [],
		holdingPeriod: 48,
		name: "",
		categoryType: { id: DepositCategoryTypeEnum.Local_Service },
		status: { id: DepositStatusEnum.Active },
		user: undefined,
	});

	// Separate state for deposit rules and exceptions (not part of deposit payload)
	const [depositRules, setDepositRules] = useState<DepositRule[]>([
		{
			type: { id: DepositRuleTypeEnum.Percentage },
			conditions: [],
			refundPolicy: "",
			threshold: "",
			amount: 0,
			deposit: { id: deposit?.id || "" },
		}
	]);

	const [depositExceptions, setDepositExceptions] = useState<DepositException[]>([]);

	// Track original data for edit mode to detect changes
	const [originalRules, setOriginalRules] = useState<DepositRule[]>([]);
	const [originalExceptions, setOriginalExceptions] = useState<DepositException[]>([]);

	const [errors, setErrors] = useState<Record<string, string>>({});

	// Helper function to normalize deposit data from different sources
	const normalizeDeposit = (dep: Deposit | DepositBackendResponse) => {
		const isBackendResponse = "categoryType" in dep && !("settings" in dep);
		
		if (isBackendResponse) {
			const backendDep = dep as DepositBackendResponse;
			return {
				id: backendDep.id,
				requireApproval: backendDep.requireApproval,
				instantRelease: backendDep.instantRelease,
				disputeWindow: backendDep.disputeWindow,
				autoRelease: backendDep.autoRelease,
				releaseConditions: backendDep.releaseConditions,
				holdingPeriod: backendDep.holdingPeriod,
				name: backendDep.name,
				categoryType: { id: parseInt(backendDep.categoryType.id) as DepositCategoryTypeEnum },
				status: { id: parseInt(backendDep.status.id) as DepositStatusEnum },
				user: backendDep.user ? { 
					id: backendDep.user.id,
					firstName: backendDep.user.firstName,
					lastName: backendDep.user.lastName,
					email: backendDep.user.email
				} : undefined,
				depositRules: backendDep.depositRules.map(rule => ({
					id: rule.id,
					type: { id: parseInt(rule.type.id) as DepositRuleTypeEnum },
					conditions: rule.conditions,
					refundPolicy: rule.refundPolicy,
					threshold: rule.threshold,
					amount: rule.amount,
					deposit: { id: rule.deposit.id },
				})),
				depositExceptions: backendDep.depositExceptions.map(exception => ({
					id: exception.id,
					conditions: exception.conditions,
					policy: exception.policy,
					type: exception.type,
					deposit: { id: exception.deposit.id },
				})),
			} as Deposit;
		}
		
		return dep as Deposit;
	};

	// Initialize form data when deposit is provided (edit mode)
	useEffect(() => {
		if (deposit && isOpen) {
			// Normalize deposit data to handle both structures
			const normalizedDeposit = normalizeDeposit(deposit);

			setFormData({
				requireApproval: normalizedDeposit.requireApproval,
				instantRelease: normalizedDeposit.instantRelease,
				disputeWindow: normalizedDeposit.disputeWindow,
				autoRelease: normalizedDeposit.autoRelease,
				releaseConditions: normalizedDeposit.releaseConditions,
				holdingPeriod: normalizedDeposit.holdingPeriod,
				name: normalizedDeposit.name,
				categoryType: normalizedDeposit.categoryType,
				status: normalizedDeposit.status,
				user: normalizedDeposit.user,
			});
			
			const rules = normalizedDeposit.depositRules || [
				{
					type: { id: DepositRuleTypeEnum.Percentage },
					conditions: [],
					refundPolicy: "",
					threshold: "",
					amount: 0,
					deposit: { id: normalizedDeposit.id },
				}
			];
			const exceptions = normalizedDeposit.depositExceptions || [];
			
			setDepositRules(rules);
			setDepositExceptions(exceptions);
			
			// Store original data for comparison
			setOriginalRules(rules);
			setOriginalExceptions(exceptions);
		} else if (!deposit && isOpen) {
			// Reset form for new deposit
			setFormData({
				requireApproval: true,
				instantRelease: false,
				disputeWindow: 24,
				autoRelease: true,
				releaseConditions: [],
				holdingPeriod: 48,
				name: "",
				categoryType: { id: DepositCategoryTypeEnum.Local_Service },
				status: { id: DepositStatusEnum.Active },
				user: undefined,
			});
			const defaultRules = [
				{
					type: { id: DepositRuleTypeEnum.Percentage },
					conditions: [],
					refundPolicy: "",
					threshold: "",
					amount: 0,
					deposit: { id: "" },
				}
			];
			setDepositRules(defaultRules);
			setDepositExceptions([]);
			
			// Clear original data for new deposit
			setOriginalRules([]);
			setOriginalExceptions([]);
		}
		setErrors({});
		dispatch(clearDepositError());
	}, [deposit, isOpen, dispatch]);

	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.name.trim()) {
			newErrors.name = "Deposit name is required";
		}

		if (formData.disputeWindow < 0) {
			newErrors.disputeWindow = "Dispute window must be 0 or greater";
		}

		if (formData.holdingPeriod < 0) {
			newErrors.holdingPeriod = "Holding period must be 0 or greater";
		}

		depositRules.forEach((rule, index) => {
			if (rule.amount < 0) {
				newErrors[`ruleAmount_${index}`] = `Rule ${index + 1} amount must be 0 or greater`;
			}
			if (!rule.refundPolicy.trim()) {
				newErrors[`ruleRefundPolicy_${index}`] = `Rule ${index + 1} refund policy is required`;
			}
			if (!rule.threshold.trim()) {
				newErrors[`ruleThreshold_${index}`] = `Rule ${index + 1} threshold is required`;
			}
		});

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		
		if (!validateForm()) {
			return;
		}

		try {
			if (isEditMode && deposit) {
				// Update main deposit
				await dispatch(updateDeposit({ id: deposit.id, data: formData })).unwrap();
				
				// Handle deposit rules updates
				await handleRuleUpdates();
				
				// Handle deposit exceptions updates
				await handleExceptionUpdates();
			} else {
				// Create deposit first
				const createdDeposit = await dispatch(createDeposit(formData)).unwrap();
				
				// Create deposit rules if any
				if (depositRules && depositRules.length > 0) {
					for (const rule of depositRules) {
						const ruleData: DepositRuleCreateDto = {
							type: rule.type,
							conditions: rule.conditions,
							refundPolicy: rule.refundPolicy,
							threshold: rule.threshold,
							amount: rule.amount,
							deposit: { id: createdDeposit.id },
						};
						await dispatch(createDepositRule(ruleData)).unwrap();
					}
				}
				
				// Create deposit exceptions if any
				if (depositExceptions && depositExceptions.length > 0) {
					for (const exception of depositExceptions) {
						const exceptionData: DepositExceptionCreateDto = {
							conditions: exception.conditions,
							policy: exception.policy,
							type: exception.type,
							deposit: { id: createdDeposit.id },
						};
						await dispatch(createDepositException(exceptionData)).unwrap();
					}
				}
			}
			
			// Fetch latest deposits from backend to show updated data
			await dispatch(fetchDeposits(undefined)).unwrap();
			
			onClose();
		} catch (error) {
			console.error("Error saving deposit:", error);
		}
	};

	const handleInputChange = (field: string, value: any) => {
		setFormData(prev => ({
			...prev,
			[field]: value,
		}));
		
		// Clear error when user starts typing
		if (errors[field]) {
			setErrors(prev => ({
				...prev,
				[field]: "",
			}));
		}
	};

	const addReleaseCondition = () => {
		setFormData(prev => ({
			...prev,
			releaseConditions: [...prev.releaseConditions, ""]
		}));
	};

	const removeReleaseCondition = (index: number) => {
		setFormData(prev => ({
			...prev,
			releaseConditions: prev.releaseConditions.filter((_, i) => i !== index)
		}));
	};

	const updateReleaseCondition = (index: number, value: string) => {
		setFormData(prev => ({
			...prev,
			releaseConditions: prev.releaseConditions.map((condition, i) => 
				i === index ? value : condition
			)
		}));
	};

	// Deposit Rule Functions
	const addRule = () => {
		setDepositRules(prev => [
			...prev,
			{
				type: { id: DepositRuleTypeEnum.Percentage },
				conditions: [],
				refundPolicy: "",
				threshold: "",
				amount: 0,
				deposit: { id: deposit?.id || "" },
			}
		]);
	};

	const removeRule = (index: number) => {
		if (depositRules.length > 1) {
			setDepositRules(prev => prev.filter((_, i) => i !== index));
		}
	};

	const updateRule = (index: number, field: string, value: any) => {
		setDepositRules(prev => prev.map((rule, i) => 
			i === index ? { ...rule, [field]: value } : rule
		));
		
		if (errors[`ruleAmount_${index}`] || errors[`ruleRefundPolicy_${index}`] || errors[`ruleThreshold_${index}`]) {
			setErrors(prev => ({
				...prev,
				[`ruleAmount_${index}`]: "",
				[`ruleRefundPolicy_${index}`]: "",
				[`ruleThreshold_${index}`]: "",
			}));
		}
	};

	const addRuleCondition = (ruleIndex: number) => {
		setDepositRules(prev => prev.map((rule, i) => 
			i === ruleIndex 
				? { ...rule, conditions: [...rule.conditions, ""] }
				: rule
		));
	};

	const removeRuleCondition = (ruleIndex: number, conditionIndex: number) => {
		setDepositRules(prev => prev.map((rule, i) => 
			i === ruleIndex 
				? { ...rule, conditions: rule.conditions.filter((_, ci) => ci !== conditionIndex) }
				: rule
		));
	};

	const updateRuleCondition = (ruleIndex: number, conditionIndex: number, value: string) => {
		setDepositRules(prev => prev.map((rule, i) => 
			i === ruleIndex 
				? { 
					...rule, 
					conditions: rule.conditions.map((condition, ci) => 
						ci === conditionIndex ? value : condition
					)
				}
				: rule
		));
	};

	// Deposit Exception Functions
	const addException = () => {
		setDepositExceptions(prev => [
			...prev,
			{
				conditions: [],
				policy: "",
				type: "",
				deposit: { id: deposit?.id || "" },
			}
		]);
	};

	const removeException = (index: number) => {
		setDepositExceptions(prev => prev.filter((_, i) => i !== index));
	};

	const updateException = (index: number, field: string, value: any) => {
		setDepositExceptions(prev => prev.map((exception, i) => 
			i === index ? { ...exception, [field]: value } : exception
		));
	};

	const addExceptionCondition = (exceptionIndex: number) => {
		setDepositExceptions(prev => prev.map((exception, i) => 
			i === exceptionIndex 
				? { ...exception, conditions: [...exception.conditions, ""] }
				: exception
		));
	};

	const removeExceptionCondition = (exceptionIndex: number, conditionIndex: number) => {
		setDepositExceptions(prev => prev.map((exception, i) => 
			i === exceptionIndex 
				? { ...exception, conditions: exception.conditions.filter((_, ci) => ci !== conditionIndex) }
				: exception
		));
	};

	const updateExceptionCondition = (exceptionIndex: number, conditionIndex: number, value: string) => {
		setDepositExceptions(prev => prev.map((exception, i) => 
			i === exceptionIndex 
				? { 
					...exception, 
					conditions: exception.conditions.map((condition, ci) => 
						ci === conditionIndex ? value : condition
					)
				}
				: exception
		));
	};

	// Helper functions to handle rule and exception updates
	const handleRuleUpdates = async () => {
		if (!deposit) return;

		const currentRules = depositRules;
		const originalRulesData = originalRules;

		// Find rules to delete (exist in original but not in current)
		const rulesToDelete = originalRulesData.filter(originalRule => 
			!currentRules.some(currentRule => 
				// Check if rule has an ID (exists in backend) and matches
				originalRule.id && originalRule.id === currentRule.id
			)
		);

		// Delete removed rules
		for (const rule of rulesToDelete) {
			if (rule.id) {
				await dispatch(deleteDepositRule(rule.id)).unwrap();
			}
		}

		// Find rules to update (exist in both but data changed)
		const rulesToUpdate = currentRules.filter(currentRule => {
			const originalRule = originalRulesData.find(or => 
				or.id && or.id === currentRule.id
			);
			if (!originalRule) return false;
			
			return (
				originalRule.type.id !== currentRule.type.id ||
				originalRule.amount !== currentRule.amount ||
				originalRule.refundPolicy !== currentRule.refundPolicy ||
				originalRule.threshold !== currentRule.threshold ||
				JSON.stringify(originalRule.conditions) !== JSON.stringify(currentRule.conditions)
			);
		});

		// Update changed rules
		for (const rule of rulesToUpdate) {
			if (rule.id) {
				const updateData = {
					type: rule.type,
					conditions: rule.conditions,
					refundPolicy: rule.refundPolicy,
					threshold: rule.threshold,
					amount: rule.amount,
				};
				await dispatch(updateDepositRule({ id: rule.id, data: updateData })).unwrap();
			}
		}

		// Find rules to create (new rules without ID)
		const rulesToCreate = currentRules.filter(rule => !rule.id);

		// Create new rules
		for (const rule of rulesToCreate) {
			const ruleData: DepositRuleCreateDto = {
				type: rule.type,
				conditions: rule.conditions,
				refundPolicy: rule.refundPolicy,
				threshold: rule.threshold,
				amount: rule.amount,
				deposit: { id: deposit.id },
			};
			await dispatch(createDepositRule(ruleData)).unwrap();
		}
	};

	const handleExceptionUpdates = async () => {
		if (!deposit) return;

		const currentExceptions = depositExceptions;
		const originalExceptionsData = originalExceptions;

		// Find exceptions to delete (exist in original but not in current)
		const exceptionsToDelete = originalExceptionsData.filter(originalException => 
			!currentExceptions.some(currentException => 
				// Check if exception has an ID (exists in backend) and matches
				originalException.id && originalException.id === currentException.id
			)
		);

		// Delete removed exceptions
		for (const exception of exceptionsToDelete) {
			if (exception.id) {
				await dispatch(deleteDepositException(exception.id)).unwrap();
			}
		}

		// Find exceptions to update (exist in both but data changed)
		const exceptionsToUpdate = currentExceptions.filter(currentException => {
			const originalException = originalExceptionsData.find(oe => 
				oe.id && oe.id === currentException.id
			);
			if (!originalException) return false;
			
			return (
				originalException.type !== currentException.type ||
				originalException.policy !== currentException.policy ||
				JSON.stringify(originalException.conditions) !== JSON.stringify(currentException.conditions)
			);
		});

		// Update changed exceptions
		for (const exception of exceptionsToUpdate) {
			if (exception.id) {
				const updateData = {
					type: exception.type,
					policy: exception.policy,
					conditions: exception.conditions,
				};
				await dispatch(updateDepositException({ id: exception.id, data: updateData })).unwrap();
			}
		}

		// Find exceptions to create (new exceptions without ID)
		const exceptionsToCreate = currentExceptions.filter(exception => !exception.id);

		// Create new exceptions
		for (const exception of exceptionsToCreate) {
			const exceptionData: DepositExceptionCreateDto = {
				type: exception.type,
				policy: exception.policy,
				conditions: exception.conditions,
				deposit: { id: deposit.id },
			};
			await dispatch(createDepositException(exceptionData)).unwrap();
		}
	};

	const getEnumDisplayName = (enumType: any, value: number): string => {
		const enumKey = Object.keys(enumType).find(key => enumType[key] === value);
		return enumKey ? enumKey.replace(/_/g, " ") : "";
	};

	if (!isOpen) return null;

	return (
		<AnimatePresence>
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
			>
				<motion.div
					initial={{ scale: 0.95 }}
					animate={{ scale: 1 }}
					exit={{ scale: 0.95 }}
					className="bg-white rounded-xl max-w-4xl w-full p-6 relative max-h-[90vh] overflow-y-auto"
				>
					<button
						onClick={onClose}
						className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
					>
						<X className="h-5 w-5" />
					</button>

					<div className="pr-8">
						<h2 className="text-2xl font-bold mb-6">
							{isEditMode ? "Edit Deposit Rule" : "Add Deposit Rule"}
						</h2>

						{error && (
							<div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg">
								{error}
							</div>
						)}

						<form onSubmit={handleSubmit} className="space-y-6">
							{/* Basic Information */}
							<Card className="p-6">
								<h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
									<Settings className="h-5 w-5" />
									Basic Information
								</h3>
								
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium mb-2">
											Deposit Name *
										</label>
										<Input
											type="text"
											value={formData.name}
											onChange={(e) => handleInputChange("name", e.target.value)}
											placeholder="Enter deposit name"
											className={errors.name ? "border-red-500" : ""}
										/>
										{errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium mb-2">
											Status
										</label>
										<select
											value={formData.status.id}
											onChange={(e) => handleInputChange("status", { id: parseInt(e.target.value) })}
											className="w-full p-2 border rounded-lg"
										>
											<option value={DepositStatusEnum.Active}>Active</option>
											<option value={DepositStatusEnum.Inactive}>Inactive</option>
										</select>
									</div>


									<div>
										<label className="block text-sm font-medium mb-2">
											Holding Period (hours) *
										</label>
										<Input
											type="number"
											value={formData.holdingPeriod}
											onChange={(e) => handleInputChange("holdingPeriod", parseInt(e.target.value) || 0)}
											placeholder="48"
											min="0"
											className={errors.holdingPeriod ? "border-red-500" : ""}
										/>
										{errors.holdingPeriod && <p className="text-red-500 text-sm mt-1">{errors.holdingPeriod}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium mb-2">
											Dispute Window (hours) *
										</label>
										<Input
											type="number"
											value={formData.disputeWindow}
											onChange={(e) => handleInputChange("disputeWindow", parseInt(e.target.value) || 0)}
											placeholder="24"
											min="0"
											className={errors.disputeWindow ? "border-red-500" : ""}
										/>
										{errors.disputeWindow && <p className="text-red-500 text-sm mt-1">{errors.disputeWindow}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium mb-2">
											User (Optional)
										</label>
										<UserDropdown
											selectedUser={formData.user ? {
												id: formData.user.id,
												firstName: formData.user.firstName || "",
												lastName: formData.user.lastName || "",
												email: formData.user.email || "",
											} : null}
											onUserSelect={(user) => handleInputChange("user", user ? { 
												id: user.id,
												firstName: user.firstName,
												lastName: user.lastName,
												email: user.email
											} as DepositUserRef : undefined)}
											placeholder="Select a user (optional)"
											className="w-full"
											roles={[
												{ id: RoleEnum.client}
												
											]}
										/>
									</div>
								</div>
							</Card>

							{/* Settings */}
							<Card className="p-6">
								<h3 className="text-lg font-semibold mb-4">Settings</h3>
								
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div className="flex items-center gap-2">
										<input
											type="checkbox"
											id="requireApproval"
											checked={formData.requireApproval}
											onChange={(e) => handleInputChange("requireApproval", e.target.checked)}
											className="h-4 w-4"
										/>
										<label htmlFor="requireApproval" className="text-sm font-medium">
											Require Approval
										</label>
									</div>

									<div className="flex items-center gap-2">
										<input
											type="checkbox"
											id="instantRelease"
											checked={formData.instantRelease}
											onChange={(e) => handleInputChange("instantRelease", e.target.checked)}
											className="h-4 w-4"
										/>
										<label htmlFor="instantRelease" className="text-sm font-medium">
											Instant Release
										</label>
									</div>

									<div className="flex items-center gap-2">
										<input
											type="checkbox"
											id="autoRelease"
											checked={formData.autoRelease}
											onChange={(e) => handleInputChange("autoRelease", e.target.checked)}
											className="h-4 w-4"
										/>
										<label htmlFor="autoRelease" className="text-sm font-medium">
											Auto Release
										</label>
									</div>
								</div>
							</Card>

							{/* Release Conditions */}
							<Card className="p-6">
								<div className="flex justify-between items-center mb-4">
									<h3 className="text-lg font-semibold flex items-center gap-2">
										<Check className="h-5 w-5" />
										Release Conditions
									</h3>
									<Button
										type="button"
										onClick={addReleaseCondition}
										className="flex items-center gap-2"
									>
										<Plus className="h-4 w-4" />
										Add Condition
									</Button>
								</div>

								<div className="space-y-2">
									{formData.releaseConditions.map((condition, index) => (
										<div key={index} className="flex gap-2">
											<Input
												type="text"
												value={condition}
												onChange={(e) => updateReleaseCondition(index, e.target.value)}
												placeholder="Enter release condition"
												className="flex-1"
											/>
											<Button
												type="button"
												variant="ghost"
												size="sm"
												onClick={() => removeReleaseCondition(index)}
												className="text-red-600"
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</div>
									))}
								</div>
							</Card>

							{/* Deposit Rules */}
							<Card className="p-6">
								<div className="flex justify-between items-center mb-4">
									<h3 className="text-lg font-semibold flex items-center gap-2">
										<Shield className="h-5 w-5" />
										Deposit Rules
									</h3>
									<Button
										type="button"
										onClick={addRule}
										className="flex items-center gap-2"
									>
										<Plus className="h-4 w-4" />
										Add Rule
									</Button>
								</div>

								<div className="space-y-4">
									{depositRules.map((rule, index) => (
										<div key={index} className="p-4 border rounded-lg">
											<div className="flex justify-between items-start mb-4">
												<h4 className="font-medium">Rule {index + 1}</h4>
												{depositRules.length > 1 && (
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => removeRule(index)}
														className="text-red-600"
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												)}
											</div>

											<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
												<div>
													<label className="block text-sm font-medium mb-2">
														Rule Type
													</label>
													<select
														value={rule.type.id}
														onChange={(e) => updateRule(index, "type", { id: parseInt(e.target.value) })}
														className="w-full p-2 border rounded-lg"
													>
														<option value={DepositRuleTypeEnum.Fixed_Amount}>Fixed Amount</option>
														<option value={DepositRuleTypeEnum.Percentage}>Percentage</option>
													</select>
												</div>

												<div>
													<label className="block text-sm font-medium mb-2">
														{rule.type.id === DepositRuleTypeEnum.Percentage ? "Percentage" : "Amount"}
													</label>
													<Input
														type="number"
														value={rule.amount}
														onChange={(e) => updateRule(index, "amount", parseFloat(e.target.value) || 0)}
														placeholder="0"
														min="0"
														step={rule.type.id === DepositRuleTypeEnum.Percentage ? "0.1" : "1"}
														className={errors[`ruleAmount_${index}`] ? "border-red-500" : ""}
													/>
													{errors[`ruleAmount_${index}`] && <p className="text-red-500 text-sm mt-1">{errors[`ruleAmount_${index}`]}</p>}
												</div>

												<div>
													<label className="block text-sm font-medium mb-2">
														Threshold
													</label>
													<Input
														type="text"
														value={rule.threshold}
														onChange={(e) => updateRule(index, "threshold", e.target.value)}
														placeholder={`e.g., Orders under ${getDynamicCurrencySymbol()}500`}
														className={errors[`ruleThreshold_${index}`] ? "border-red-500" : ""}
													/>
													{errors[`ruleThreshold_${index}`] && <p className="text-red-500 text-sm mt-1">{errors[`ruleThreshold_${index}`]}</p>}
												</div>
											</div>

											<div className="mb-4">
												<label className="block text-sm font-medium mb-2">
													Refund Policy *
												</label>
												<Input
													type="text"
													value={rule.refundPolicy}
													onChange={(e) => updateRule(index, "refundPolicy", e.target.value)}
													placeholder="e.g., Fully refundable within 24 hours"
													className={errors[`ruleRefundPolicy_${index}`] ? "border-red-500" : ""}
												/>
												{errors[`ruleRefundPolicy_${index}`] && <p className="text-red-500 text-sm mt-1">{errors[`ruleRefundPolicy_${index}`]}</p>}
											</div>

											<div>
												<div className="flex justify-between items-center mb-2">
													<label className="block text-sm font-medium">
														Conditions
													</label>
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => addRuleCondition(index)}
														className="flex items-center gap-1"
													>
														<Plus className="h-3 w-3" />
														Add Condition
													</Button>
												</div>
												
												<div className="space-y-2">
													{rule.conditions.map((condition, conditionIndex) => (
														<div key={conditionIndex} className="flex gap-2">
															<Input
																type="text"
																value={condition}
																onChange={(e) => updateRuleCondition(index, conditionIndex, e.target.value)}
																placeholder="Enter condition"
																className="flex-1"
															/>
															<Button
																type="button"
																variant="ghost"
																size="sm"
																onClick={() => removeRuleCondition(index, conditionIndex)}
																className="text-red-600"
															>
																<Trash2 className="h-4 w-4" />
															</Button>
														</div>
													))}
												</div>
											</div>
										</div>
									))}
								</div>
							</Card>

							{/* Deposit Exceptions */}
							<Card className="p-6">
								<div className="flex justify-between items-center mb-4">
									<h3 className="text-lg font-semibold flex items-center gap-2">
										<AlertCircle className="h-5 w-5" />
										Deposit Exceptions
									</h3>
									<Button
										type="button"
										onClick={addException}
										className="flex items-center gap-2"
									>
										<Plus className="h-4 w-4" />
										Add Exception
									</Button>
								</div>

								<div className="space-y-4">
									{depositExceptions.map((exception, index) => (
										<div key={index} className="p-4 border rounded-lg">
											<div className="flex justify-between items-start mb-4">
												<h4 className="font-medium">Exception {index + 1}</h4>
												<Button
													type="button"
													variant="ghost"
													size="sm"
													onClick={() => removeException(index)}
													className="text-red-600"
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>

											<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
												<div>
													<label className="block text-sm font-medium mb-2">
														Exception Type
													</label>
													<Input
														type="text"
														value={exception.type}
														onChange={(e) => updateException(index, "type", e.target.value)}
														placeholder="e.g., Emergency Service"
													/>
												</div>

												<div>
													<label className="block text-sm font-medium mb-2">
														Policy
													</label>
													<Input
														type="text"
														value={exception.policy}
														onChange={(e) => updateException(index, "policy", e.target.value)}
														placeholder="e.g., No deposit required"
													/>
												</div>
											</div>

											<div>
												<div className="flex justify-between items-center mb-2">
													<label className="block text-sm font-medium">
														Conditions
													</label>
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => addExceptionCondition(index)}
														className="flex items-center gap-1"
													>
														<Plus className="h-3 w-3" />
														Add Condition
													</Button>
												</div>
												
												<div className="space-y-2">
													{exception.conditions.map((condition, conditionIndex) => (
														<div key={conditionIndex} className="flex gap-2">
															<Input
																type="text"
																value={condition}
																onChange={(e) => updateExceptionCondition(index, conditionIndex, e.target.value)}
																placeholder="Enter condition"
																className="flex-1"
															/>
															<Button
																type="button"
																variant="ghost"
																size="sm"
																onClick={() => removeExceptionCondition(index, conditionIndex)}
																className="text-red-600"
															>
																<Trash2 className="h-4 w-4" />
															</Button>
														</div>
													))}
												</div>
											</div>
										</div>
									))}
								</div>
							</Card>

							{/* Form Actions */}
							<div className="flex justify-end gap-3 pt-4">
								<Button
									type="button"
									variant="outline"
									onClick={onClose}
									disabled={loading}
								>
									Cancel
								</Button>
								<Button
									type="submit"
									disabled={loading}
									className="flex items-center gap-2"
								>
									{loading ? "Saving..." : isEditMode ? "Update Deposit" : "Create Deposit"}
								</Button>
							</div>
						</form>
					</div>
				</motion.div>
			</motion.div>
		</AnimatePresence>
	);
};

export default DepositDialog;

